import { Platform } from 'react-native';
import type {
  HomeReview,
  Photo,
  ReviewItem,
  ReviewResponse,
  UploadedPhoto,
} from '@exhale/contracts';
import {
  PhotoUploadInput,
  PresignedPhotoUrl,
} from './reviewPhotoApi';
import { ReviewResponsePhotoCache } from './reviewResponsePhotoCache';

type ReviewPhotoServiceApi = {
  getPresignedUrls(photoIds: number[], expiresIn?: number): Promise<PresignedPhotoUrl[]>;
  uploadPhotos(
    reviewId: number,
    responseId: number,
    itemId: number,
    photos: PhotoUploadInput[],
    questionId?: string
  ): Promise<UploadedPhoto[]>;
  uploadPhoto(
    reviewId: number,
    itemId: number,
    photoData: PhotoUploadInput
  ): Promise<UploadedPhoto[]>;
  deletePhoto(photoId: number): Promise<void>;
  updatePhotoNote(photoId: number, note: string): Promise<Photo>;
};

export type ReviewPhotoServiceDependencies = {
  photoApi: ReviewPhotoServiceApi;
  responsePhotoCache?: ReviewResponsePhotoCache;
  getReviewItemDetail(
    reviewId: number,
    itemId: number
  ): Promise<{ review: Partial<HomeReview>; item: ReviewItem; response: ReviewResponse | null }>;
  refreshOnlineState: () => Promise<boolean>;
  getIsOnline: () => boolean;
  isWeb?: () => boolean;
};

export class ReviewPhotoService {
  private readonly responsePhotoCache: ReviewResponsePhotoCache;

  constructor(private readonly deps: ReviewPhotoServiceDependencies) {
    this.responsePhotoCache = deps.responsePhotoCache ?? new ReviewResponsePhotoCache();
  }

  async getPhotoPresignedUrls(photoIds: number[], expiresIn?: number): Promise<PresignedPhotoUrl[]> {
    return this.deps.photoApi.getPresignedUrls(photoIds, expiresIn);
  }

  async uploadPhotos(
    reviewId: number,
    responseId: number,
    itemId: number,
    photos: PhotoUploadInput[],
    questionId?: string
  ): Promise<UploadedPhoto[]> {
    try {
      const uploaded = await this.deps.photoApi.uploadPhotos(
        reviewId,
        responseId,
        itemId,
        photos,
        questionId
      );
      this.invalidateResponsePhotoCache(responseId);
      return uploaded;
    } catch (error) {
      console.error('[ReviewService] uploadPhotos() ERROR:', error);
      throw error;
    }
  }

  async uploadPhoto(
    reviewId: number,
    itemId: number,
    photoData: PhotoUploadInput
  ): Promise<UploadedPhoto[]> {
    return this.deps.photoApi.uploadPhoto(reviewId, itemId, photoData);
  }

  async deletePhoto(photoId: number): Promise<void> {
    return this.deps.photoApi.deletePhoto(photoId);
  }

  async updatePhotoNote(photoId: number, note: string): Promise<Photo> {
    return this.deps.photoApi.updatePhotoNote(photoId, note);
  }

  invalidateResponsePhotoCache(responseId: number): void {
    this.responsePhotoCache.invalidate(responseId);
  }

  async getPhotosForQuestion(
    reviewId: number,
    responseId: number,
    itemId?: number,
    questionId?: string
  ): Promise<any[]> {
    return this.responsePhotoCache.getPhotos(responseId, questionId, async () => {
      if (!this.isWeb()) {
        await this.deps.refreshOnlineState();
      }

      return this.fetchResponseForPhotos(reviewId, responseId, itemId);
    });
  }

  private async fetchResponseForPhotos(
    reviewId: number,
    responseId: number,
    itemId?: number
  ): Promise<any | null> {
    try {
      if (typeof itemId === 'number') {
        const { response } = await this.deps.getReviewItemDetail(reviewId, itemId);
        if (response) {
          return response;
        }
      }
    } catch (error) {
      console.error('[ReviewService] Failed to load item photos via detail endpoint:', error);
    }

    console.error('[ReviewService] Response photos unavailable from item detail endpoint');
    return null;
  }

  private isWeb(): boolean {
    return this.deps.isWeb ? this.deps.isWeb() : Platform.OS === 'web';
  }
}
